import React, { Component } from "react";
import PromineoDataGrid from "components/common/controls/grid/PromineoDataGrid";
import { DataGridType } from "components/common/controls/grid/PromineoBaseDataGrid";
import {
  Column,
  IDataGridOptions,
  Paging,
  Pager,
} from "devextreme-react/data-grid";
import moment from "moment";
import { connect } from "react-redux";
import { RootState, AppDispatch } from "store/store";
import UserResponse from "interfaces/response/UserResponse";
import Status from "components/common/Status";
import { Variant } from "shared/enums/VariantEnum";
import TagControl from "components/common/TagControl";
import { EditorMode } from "shared/enums/EditorModeEnum";
import ManageUserGridActions from "./ManageUserGridActions";
import ManageUserGridActionHeader from "./ManageUserGridActionHeader";
import UserEditorPopup from "./UserEditorPopup";
import DropdownValuesResponse from "interfaces/response/DropdownValuesResponse";
import CustomStore from "devextreme/data/custom_store";
import {
  getAllUsersListAsync,
  setSelectedUser,
  deleteUser,
} from "store/slices/UserManagementSlice";
import DataSource from "devextreme/data/data_source";
import PagingParameter from "interfaces/request/PagingParameter";
import { DataGridPageSize } from "../../../../src/shared/constants/GridConstantValues";
import DeleteAccountConfirmationPopup from "../../account/DeleteAccountConfirmationPopup";
import { InactiveUserRetentionPeriod } from "shared/constants/AdministrationConstants";
import { getFullDateFromString } from "shared/utility/DateTimeUtility";

interface Props {
  dataSource?: any;
  dropdownValues?: DropdownValuesResponse[];
  dispatch?: AppDispatch;
  selectedUserDetail?: any;
}

interface State {
  isEditUserPopupVisible: boolean;
  isDeleteUserPopupVisible: boolean;
  slectedRowData: UserResponse | null;
}

export class UserDetailsGrid extends Component<Props, State> {
  state: Readonly<State> = {
    isEditUserPopupVisible: false,
    isDeleteUserPopupVisible: false,
    slectedRowData: null,
  };

  dataGridRef = React.createRef<DataGridType>();
  deleteUserWarningDayDuration = 7;

  StatusCellRender = ({ data }: { data: UserResponse }) => {
    return (
      <Status
        variant={data.isActive ? Variant.Success : Variant.Danger}
        text={data.isActive ? "Active" : "Inactive"}
      />
    );
  };

  RoleCellRender = ({ data }: { data: UserResponse }) => {
    const roleValues = data.roles;
    const resultString: string[] = roleValues.map(
      (item: { name: string }) => item.name
    );

    return <TagControl tags={resultString} />;
  };

  PermanentlyDeleteOnCellRender = ({ data }: { data: UserResponse }) => {
    const deleteOn = moment(data.inactivatedOn).add(InactiveUserRetentionPeriod, "days").format();

    const getRemainingDaysToExpire = () => {
      return moment(deleteOn).diff(moment(), "days");
    };

    const remainingDays = getRemainingDaysToExpire();

    const checkIfAboutToExpire = () => {
      return remainingDays < this.deleteUserWarningDayDuration;
    };

    const getFormattedDate = () => {
      return getFullDateFromString(deleteOn);
    };

    const getTitleTooltip = () => {
      return `Permanently delete in ${remainingDays} days.`;
    };

    return (
      <>
        {!data.isActive ? 
          (<>{checkIfAboutToExpire() ? (
            <div title={getTitleTooltip()}>
              <Status variant={Variant.Danger} text={getFormattedDate()}></Status>
            </div>
            ) : (
            <>{getFormattedDate()}</>
          )}</>) : 
        (<>
        </>)}
      </>
    );
  };

  handleEditUserClick = (data: UserResponse) => {
    this.props.dispatch!(setSelectedUser(data));
    this.setState({
      isEditUserPopupVisible: true,
    });
  };

  handleEditUserPopupClose = () => {
    this.setState({
      isEditUserPopupVisible: false,
    });
  };

  handleDeleteUserClick = (data: UserResponse) => {
    this.setState({
      isDeleteUserPopupVisible: true,
      slectedRowData: data,
    });
  };

  handleDeleteUserPopupClose = () => {
    this.setState({
      isDeleteUserPopupVisible: false,
    });
  };

  handleDeleteUserConfirm = () => {
    if (this.state.slectedRowData) {
      this.props.dispatch!(deleteUser(this.state.slectedRowData.id)).then(
        () => {
          this.setState({
            isDeleteUserPopupVisible: false,
          });
        }
      );
    }
  };

  handlePageIndexChange = (e: any) => {
    if (e.fullName === "paging.pageIndex") {
      let nextIndex: number = e.value + 1; // Adding 1 because grid is 0-indexed and our server is 1-indexed //

      let pagingParameter: PagingParameter = {
        currentPage: nextIndex,
        pageSize: DataGridPageSize,
      };

      this.props.dispatch!(getAllUsersListAsync(pagingParameter));
    }
  };

  render() {
    const dataGridOptions: IDataGridOptions = {
      dataSource: this.props.dataSource,
      remoteOperations: true, // For server-side Pagination
      onOptionChanged: this.handlePageIndexChange,
      loadPanel: { enabled: false }, // Disable Loader
    };

    return (
      <>
        <div className="mb-20">
          <ManageUserGridActionHeader />
        </div>
        <div>
          <PromineoDataGrid
            dataGridOptions={dataGridOptions}
            gridRef={this.dataGridRef}
            classNames="max-h-600px"
            noDataText="No users found.
            (Click ‘New User’ button to create new user)"
          >
            <Paging defaultPageSize={DataGridPageSize} />
            <Pager visible={true} showNavigationButtons={true} />
            <Column dataField="email" caption="Email" width="15%" />
            <Column dataField="phone" caption="Phone" width="10%" />
            <Column dataField="firstName" caption="First Name" width="15%" />
            <Column dataField="lastName" caption="Last Name" width="15%" />
            <Column
              caption="Status"
              dataField="status"
              cellRender={this.StatusCellRender}
              width="100px"
              alignment="center"
            />
            <Column
              caption="Delete On"
              alignment="center"
              width="13%"
              cellRender={this.PermanentlyDeleteOnCellRender}
            />
            <Column dataField="customerId" caption="Customer Id" width="15%" />
            <Column
              caption="User Roles"
              cellRender={this.RoleCellRender}
              cssClass="whitespace-normal"
              width="20%"
            />
            <Column
              caption="Actions"
              cssClass="w-16 lg:w-48 border"
              cellRender={(userData: any) => (
                <ManageUserGridActions
                  onEditClick={() => {
                    this.handleEditUserClick(userData.data);
                  }}
                  onDeleteClick={() => {
                    this.handleDeleteUserClick(userData.data);
                  }}
                />
              )}
            />
          </PromineoDataGrid>
        </div>
        {this.state.isEditUserPopupVisible && (
          <UserEditorPopup
            onClose={this.handleEditUserPopupClose}
            mode={EditorMode.Edit}
            user={this.props.selectedUserDetail}
          />
        )}
        {this.state.isDeleteUserPopupVisible && (
          <DeleteAccountConfirmationPopup
            onCancel={this.handleDeleteUserPopupClose}
            onApply={this.handleDeleteUserConfirm}
          />
        )}
      </>
    );
  }
}

const mapStateToProps = (state: RootState, props: Props) => {
  let customStoreBuilder = {
    key: "id",
    load: async () => {
      return {
        data: state.userManagementData.users,
        totalCount: state.userManagementData.totalItemsCount,
      };
    },
  };
  let customStore = new CustomStore(customStoreBuilder);
  let customDataSource = new DataSource({
    store: customStore,
  });

  return {
    dataSource: customDataSource,
    dropdownValues: state.dropDownData.userRoleValues,
    selectedUserDetail: state.userManagementData.selectedUserDetail,
    ...props,
  };
};

export default connect(mapStateToProps)(UserDetailsGrid);
